/**
 * HAL LITE - Version légère pour iPad Mini 2
 * Affiche l'œil de HAL avec des messages et alertes
 */
import { useState, useEffect } from 'react';
import { api } from '@/App';

const HalLite = () => {
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [halState, setHalState] = useState('idle'); // idle, thinking, alert, speaking
  const [time, setTime] = useState(new Date());

  // Messages aléatoires de HAL
  const halQuotes = [
    "Je suis complètement opérationnel.",
    "Je suis prêt à vous assister.",
    "Tous les systèmes fonctionnent normalement.",
    "La maison est sous contrôle.",
    "Je surveille les paramètres...",
    "Température stable. Énergie optimisée.",
    "Aucune anomalie détectée.",
    "Je suis HAL 3000. Comment puis-je vous aider ?",
    "Mission : Confort et sécurité.",
    "Analyse en cours...",
    "Systèmes domotiques : OK",
    "Je ne peux pas vous laisser faire ça, Dave... Je plaisante.",
    "Bonne journée. La météo est favorable.",
    "Économie d'énergie activée.",
  ];

  // Charger les vraies alertes
  const loadAlerts = async () => {
    try {
      // Vérifier MG4
      const mg4Res = await api.get('/integrations/vehicles/mg4/status').catch(() => null);
      if (mg4Res?.data?.data) {
        const mg4 = mg4Res.data.data;
        if (mg4.range_km && mg4.range_km < 50) {
          setAlerts(prev => [...prev.filter(a => a.id !== 'mg4-range'), {
            id: 'mg4-range',
            type: 'warning',
            message: `ALERTE : Autonomie MG4 faible (${mg4.range_km} km)`,
            priority: 1
          }]);
        }
        if (mg4.charging) {
          setAlerts(prev => [...prev.filter(a => a.id !== 'mg4-charge'), {
            id: 'mg4-charge',
            type: 'info',
            message: `MG4 en charge : ${mg4.battery_pct?.toFixed(0)}%`,
            priority: 2
          }]);
        }
      }

      // Vérifier la boîte aux lettres
      const mailRes = await api.get('/integrations/tuya/devices').catch(() => null);
      if (mailRes?.data) {
        const mailbox = mailRes.data.find(d => d.name?.toLowerCase().includes('courrier') || d.name?.toLowerCase().includes('lettre'));
        if (mailbox) {
          const status = {};
          (mailbox.status || []).forEach(s => { status[s.code] = s.value; });
          if (status.doorcontact_state === true) {
            setAlerts(prev => [...prev.filter(a => a.id !== 'mail'), {
              id: 'mail',
              type: 'info',
              message: "COURRIER : Vous avez du courrier !",
              priority: 1
            }]);
          }
        }
      }

      // Vérifier chauffage
      const vicareRes = await api.get('/integrations/vicare/status').catch(() => null);
      if (vicareRes?.data) {
        const vicare = vicareRes.data;
        if (vicare.heating?.burner_active) {
          setAlerts(prev => [...prev.filter(a => a.id !== 'heating'), {
            id: 'heating',
            type: 'info', 
            message: `Chauffage actif - Circuit : ${vicare.heating?.boiler_temp?.toFixed(0)}°C`,
            priority: 3
          }]);
        }
      }

    } catch (e) {
      console.log('Error loading alerts:', e);
    }
  };

  // Mettre à jour l'heure
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Charger les alertes périodiquement
  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Cycle des messages
  useEffect(() => {
    const cycleMessages = () => {
      // Priorité aux vraies alertes
      const realAlerts = alerts.filter(a => a.priority === 1);
      if (realAlerts.length > 0) {
        const alert = realAlerts[Math.floor(Math.random() * realAlerts.length)];
        setCurrentMessage({ text: alert.message, type: alert.type });
        setHalState('alert');
      } else if (alerts.length > 0 && Math.random() > 0.5) {
        const alert = alerts[Math.floor(Math.random() * alerts.length)];
        setCurrentMessage({ text: alert.message, type: alert.type });
        setHalState('speaking');
      } else {
        // Message aléatoire de HAL
        const quote = halQuotes[Math.floor(Math.random() * halQuotes.length)];
        setCurrentMessage({ text: quote, type: 'hal' });
        setHalState(Math.random() > 0.7 ? 'thinking' : 'idle');
      }
    };

    cycleMessages();
    const interval = setInterval(cycleMessages, 8000);
    return () => clearInterval(interval);
  }, [alerts]);

  // Couleur de l'œil selon l'état
  const getEyeColor = () => {
    switch(halState) {
      case 'alert': return '#ff0000';
      case 'thinking': return '#ffaa00';
      case 'speaking': return '#00ffaa';
      default: return '#ff3333';
    }
  };

  const getGlowIntensity = () => {
    switch(halState) {
      case 'alert': return '0 0 100px #ff0000, 0 0 200px #ff000080';
      case 'thinking': return '0 0 60px #ffaa00, 0 0 120px #ffaa0060';
      default: return '0 0 60px #ff3333, 0 0 120px #ff333360';
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 overflow-hidden">
      {/* Header avec heure */}
      <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
        <div className="text-red-500 font-mono text-sm opacity-60">HAL 3000</div>
        <div className="text-red-500 font-mono text-xl">
          {time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="text-red-500 font-mono text-sm opacity-60">
          {time.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
        </div>
      </div>

      {/* Œil de HAL */}
      <div className="relative mb-8">
        {/* Anneau extérieur */}
        <div 
          className="w-48 h-48 rounded-full border-4 border-gray-800 flex items-center justify-center"
          style={{
            background: 'radial-gradient(circle, #1a1a1a 0%, #000000 100%)',
          }}
        >
          {/* Anneau intermédiaire */}
          <div 
            className="w-40 h-40 rounded-full border-2 border-gray-700 flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle, #0d0d0d 0%, #000000 100%)',
            }}
          >
            {/* Œil rouge central */}
            <div
              className={`w-24 h-24 rounded-full transition-all duration-1000 ${halState === 'alert' ? 'animate-pulse' : ''}`}
              style={{
                background: `radial-gradient(circle, ${getEyeColor()} 0%, #990000 50%, #330000 100%)`,
                boxShadow: getGlowIntensity(),
              }}
            >
              {/* Reflet */}
              <div 
                className="w-6 h-6 rounded-full bg-white/30 mt-4 ml-4"
                style={{ filter: 'blur(2px)' }}
              />
            </div>
          </div>
        </div>

        {/* Indicateurs d'état autour de l'œil */}
        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex gap-2">
          {['idle', 'thinking', 'speaking', 'alert'].map(state => (
            <div
              key={state}
              className={`w-2 h-2 rounded-full transition-all ${
                halState === state 
                  ? state === 'alert' ? 'bg-red-500' : state === 'thinking' ? 'bg-yellow-500' : 'bg-green-500'
                  : 'bg-gray-700'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Zone de message */}
      <div className="w-full max-w-md text-center min-h-24">
        {currentMessage && (
          <div 
            className={`px-6 py-4 rounded-lg transition-all duration-500 ${
              currentMessage.type === 'warning' ? 'bg-red-900/30 border border-red-500/50' :
              currentMessage.type === 'info' ? 'bg-cyan-900/30 border border-cyan-500/50' :
              'bg-gray-900/30 border border-gray-700/50'
            }`}
          >
            <p className={`font-mono text-lg ${
              currentMessage.type === 'warning' ? 'text-red-400' :
              currentMessage.type === 'info' ? 'text-cyan-400' :
              'text-gray-300'
            }`}>
              {currentMessage.text}
            </p>
          </div>
        )}
      </div>

      {/* Indicateurs système en bas */}
      <div className="absolute bottom-4 left-4 right-4">
        <div className="flex justify-center gap-8 text-xs font-mono text-gray-600">
          <span>SYS: OK</span>
          <span>NET: OK</span>
          <span>SEC: OK</span>
        </div>
      </div>

      {/* Lien discret vers version complète */}
      <a 
        href="/dashboard" 
        className="absolute bottom-4 right-4 text-gray-700 text-xs font-mono hover:text-gray-500"
      >
        [VERSION COMPLÈTE]
      </a>
    </div>
  );
};

export default HalLite;
